'use client';

import {
    Checkbox,
    FormGroup,
    FormInputErrors,
    FormLabel,
    Input,
    InputSelect,
    NumberInput,
    RadioButton,
    ButtonLink,
} from '~/components/primitives';
import Alert from '~/components/layout/Alert';
import { I18N_CONF } from '@funkey/locales/config';
import { z } from 'zod';
import { CART_DURATION_MULTIPLE_DAYS, CART_DURATION_TYPES } from '@funkey/constants';
import type { I18n } from '@funkey/locales';
import type { CartDuration } from '@funkey/types/graphql/graphql';
import { useEffect, useMemo } from 'react';
import { FormGroupHorizontal } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import type { FormState } from '~/modules/form';
import Title from '~/components/utils/Title';
import { DatePicker } from '~/components/primitives/DatePicker';
import { GoogleMapsAutocomplete } from '~/features/google/maps';
import { DateTimeSDK } from '@funkey/shared';
import {
    CACHED_PARTICIPANTS_FILTER,
    MAX_PARTICIPANTS,
} from '~/features/activities/search/client/context';
import { usePortalCtx } from '~/features/global/portal';

/** Stable reference — a new array each render remounts Radix Select's native input and can re-fire onValueChange in a loop. */
const CHECKOUT_ADDRESS_RADIUS_KM_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
    value: String(i * 10),
    label: !i ? '--' : `+ ${i * 10} km`,
}));

export namespace CheckoutInfoFieldset {
    export type FormValues = Checkout_Info_DatesFieldset.FormValues &
        Checkout_Info_LocationsFieldset.FormValues & {
            participants: number | undefined;
            budgetType: string | null;
            budget: number | undefined;
            dateTimeIndication: string | null;
        };

    export function defaultValues(): FormValues {
        return {
            startsAt: null,
            endsAt: null,
            startTime: '',
            participants: CACHED_PARTICIPANTS_FILTER.getValue(),
            address: '',
            budget: 100,
            durationType: 'HALF_DAY',
            userProvidesLocation: false,
            budgetType: null,
            radius: 10,
            dateTimeIndication: null,
        };
    }

    export const Component: React.FC<{
        i18n: I18n;
        formState: FormState.Subform<CheckoutInfoFieldset.FormValues>;
        /** Show the "select an activity" warning for groups under 15 participants. Only relevant on /contact. */
        requiresActivitySelection?: boolean;
    }> = props => {
        const isMultipleDays =
            props.formState.values.durationType === 'MULTIPLE_DAYS' ||
            props.formState.values.durationType === 'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY';

        return (
            <fieldset className="flex flex-col gap-7" data-testid="checkout-info-form">
                <Title as="legend" size="xl" weight="bold" className="mb-10">
                    {props.i18n._('Who, when, where?')}
                </Title>

                <Checkout_Info_DatesFieldset
                    i18n={props.i18n}
                    formState={props.formState.$subform<Checkout_Info_DatesFieldset.FormValues>()}
                    isMultipleDays={isMultipleDays}
                />

                <FormGroupHorizontal
                    label={props.i18n._('Participant count')}
                    required
                    description={props.i18n._(
                        'The actual number of participants can be changed up to 14 days before the teambuilding day',
                    )}
                    labelSize="md"
                    variant="no_spacing"
                    htmlFor="participants"
                >
                    <div className="flex w-full min-w-0 max-w-full flex-col gap-3 sm:max-w-[min(100%,16rem)]">
                        <NumberInput
                            id="participants"
                            className="min-w-[120px] text-center"
                            allowUndefined
                            integerOnly
                            min={0}
                            max={MAX_PARTICIPANTS}
                            step={1}
                            data-testid="checkout-participants"
                            {...props.formState.getInputProps('participants')}
                        />
                        <FormInputErrors
                            errors={props.formState.getErrorMessages('participants')}
                        />
                    </div>
                </FormGroupHorizontal>

                {props.requiresActivitySelection &&
                    typeof props.formState.values.participants === 'number' &&
                    props.formState.values.participants < 15 && (
                        <Alert
                            className="mb-0 flex-col xs:flex-row"
                            variant="warning"
                            title={props.i18n._('checkout.info.participants.min')}
                        >
                            <ButtonLink
                                className="ml-auto"
                                href={I18N_CONF.getTranslatedUrl('teambuildings', {
                                    locale: props.i18n.locale,
                                })}
                                variant="tertiary"
                                size="sm"
                                iconBefore="activity-selection"
                            >
                                {props.i18n._('Browse activities')}
                            </ButtonLink>
                        </Alert>
                    )}

                <div className="items-center gap-5 md:flex md:justify-between">
                    <div className="ga flex flex-col gap-1">
                        <FormLabel
                            htmlFor="budget"
                            size="md"
                            className="mb-3 sm:mb-0"
                            required={
                                typeof props.formState.values.participants === 'number' &&
                                props.formState.values.participants < 20
                            }
                        >
                            {props.i18n._('Budget')}
                        </FormLabel>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="flex items-center gap-3">
                                <RadioButton
                                    onChange={() =>
                                        props.formState.handleChange(
                                            'budgetType',
                                            'Budget Per Person',
                                        )
                                    }
                                    value={props.i18n._('checkout.info.budget_type.per_person')}
                                    checked={
                                        props.formState.values.budgetType === 'Budget Per Person'
                                    }
                                    id="budget-per-person"
                                />
                                <FormLabel htmlFor="budget-per-person" size="sm" weight="regular">
                                    {props.i18n._('checkout.info.budget_type.per_person')}
                                </FormLabel>
                            </div>
                            <div className="flex items-center gap-3">
                                <RadioButton
                                    onChange={() =>
                                        props.formState.handleChange('budgetType', 'Total Budget')
                                    }
                                    value={props.i18n._('checkout.info.budget_type.total')}
                                    checked={props.formState.values.budgetType === 'Total Budget'}
                                    id="budget-total"
                                />
                                <FormLabel htmlFor="budget-total" size="sm" weight="regular">
                                    {props.i18n._('checkout.info.budget_type.total')}
                                </FormLabel>
                            </div>
                            <div className="flex items-center gap-3">
                                <RadioButton
                                    onChange={() => {
                                        props.formState.handleChange('budgetType', null);
                                        props.formState.handleChange('budget', undefined);
                                    }}
                                    value={props.i18n._('checkout.info.budget_type.none')}
                                    checked={!props.formState.values.budgetType}
                                    id="budget-none"
                                />
                                <FormLabel htmlFor="budget-none" size="sm" weight="regular">
                                    {props.i18n._('checkout.info.budget_type.none')}
                                </FormLabel>
                            </div>
                        </div>
                    </div>
                    {props.formState.values.budgetType && (
                        <div className="mt-3 sm:mt-1 md:mt-0">
                            <NumberInput
                                {...props.formState.getInputProps('budget')}
                                id="budget"
                                allowUndefined={
                                    !props.formState.values.participants ||
                                    props.formState.values.participants >= 20
                                }
                                className="text-center lg:w-40"
                                min={0}
                                step={1}
                                prefix="€"
                            />
                        </div>
                    )}
                </div>
                <FormInputErrors
                    className="-mt-5"
                    errors={props.formState.getErrorMessages('budgetType')}
                />
                <FormInputErrors
                    className="-mt-5"
                    errors={props.formState.getErrorMessages('budget')}
                />

                <Checkout_Info_LocationsFieldset.Component
                    formState={props.formState.$subform<Checkout_Info_LocationsFieldset.FormValues>()}
                    i18n={props.i18n}
                />
            </fieldset>
        );
    };

    export function parseStartsAt(
        startDate: string /* date ISO string */ | null,
        time: string | null,
    ) {
        if (!startDate) return null;

        const date = new Date(startDate);

        if (time) {
            try {
                const { hours, minutes } = DateTimeSDK.ISOTime.decode(
                    DateTimeSDK.ISOTime.CODEC.in.parse(time),
                );
                date.setHours(hours);
                date.setMinutes(minutes);
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    throw e;
                } else {
                    console.error(e);
                }
            }
        }

        return date;
    }

    export const useValidation = (
        i18n: I18n,
    ): FormState.Validation<CheckoutInfoFieldset.FormValues> => {
        const portal = usePortalCtx();

        return useMemo(
            () => ({
                durationType: {
                    validate: value => z.enum(CART_DURATION_TYPES).safeParse(value).success,
                    message: i18n._('Please select the duration type'),
                },
                participants: {
                    validate: value => typeof value === 'number' && value > 1,
                    message: i18n._('Participants are required'),
                },
                startsAt: [
                    {
                        validate: value => z.string().min(1).safeParse(value).success,
                        message: i18n._('Start date is required'),
                    },
                    {
                        validate: value => z.coerce.date().safeParse(value).success,
                        message: i18n._('Start date is required'),
                    },
                ],
                endsAt: [
                    {
                        validate: value =>
                            value === null || z.coerce.date().safeParse(value).success,
                        message: i18n._('End date is not correct'),
                    },
                    {
                        validate: (value, allValues) =>
                            z.coerce
                                .date()
                                .refine(endsAt => {
                                    if (
                                        allValues.durationType !== CART_DURATION_MULTIPLE_DAYS ||
                                        endsAt === undefined ||
                                        !allValues.startsAt
                                    ) {
                                        return true;
                                    }

                                    const startsAt = new Date(allValues.startsAt);
                                    return !isNaN(startsAt.getTime()) && endsAt >= startsAt;
                                })
                                .optional()
                                .safeParse(value).success,
                        message: i18n._('End date cannot be before start date'),
                    },
                ],
                startTime: {
                    validate: value => z.string().min(5).safeParse(value).success,
                    message: i18n._('Start time is required'),
                },
                budgetType: {
                    validate: (value, allValues) => {
                        if (
                            portal.portal === 'WEB' &&
                            typeof allValues.participants === 'number' &&
                            allValues.participants < 20
                        ) {
                            return value !== null && value !== undefined;
                        }
                        return true;
                    },
                    message: i18n._('checkout.info.budget_type.required'),
                },
                budget: [
                    {
                        validate: (value, allValues) => {
                            if (
                                portal.portal === 'WEB' &&
                                typeof allValues.participants === 'number' &&
                                allValues.participants < 20
                            ) {
                                return typeof value === 'number' && value > 0;
                            }
                            return z.number().optional().safeParse(value).success;
                        },
                        message: i18n._('checkout.info.budget.required'),
                    },
                    {
                        validate: value => z.number().optional().safeParse(value).success,
                        message: i18n._('Budget must be a number'),
                    },
                ],
                userProvidesLocation: {
                    validate: value => typeof value === 'boolean',
                    message: i18n._('Location choice is required'),
                },
            }),
            [portal.portal, i18n],
        );
    };
}

declare namespace Checkout_Info_DatesFieldset {
    export type FormValues = {
        durationType: CartDuration;
        /** Date ISO string
         * @example `2025-07-22T15:43:48.245Z` */
        startsAt: string | null;
        /** Time string
         * @example `11:30` */
        startTime: string | null;
        /** Date ISO string */
        endsAt: string | null;
        /** AB testing field for date/time indication */
        dateTimeIndication: string | null;
    };
}
const Checkout_Info_DatesFieldset: React.FC<{
    formState: FormState.Subform<Checkout_Info_DatesFieldset.FormValues>;
    i18n: I18n;
    isMultipleDays: boolean;
}> = props => {
    useEffect(() => {
        if (!props.isMultipleDays) {
            props.formState.handleChange('endsAt', null);
        } else if (!props.formState.values.endsAt && props.formState.values.startsAt?.length) {
            props.formState.handleChange('endsAt', props.formState.values.startsAt);
        }
    }, [props.isMultipleDays]);

    return (
        <>
            <div className="flex flex-col gap-3">
                <FormLabel className="block font-medium leading-6 text-blue-500" size="md">
                    {props.i18n._('How long will the teambuiling last?')}
                </FormLabel>
                <div className="flex flex-col gap-x-5 gap-y-3 sm:flex-row">
                    {CART_DURATION_TYPES.map(value =>
                        value === 'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY' ? null : (
                            <div key={value} className="flex items-start gap-x-3 capitalize">
                                <RadioButton
                                    {...props.formState.getNativeInputProps('durationType')}
                                    onChange={() =>
                                        props.formState.handleChange('durationType', value)
                                    }
                                    id={`durationType-${value}`}
                                    checked={
                                        props.formState.values.durationType ===
                                        'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY'
                                            ? value === 'MULTIPLE_DAYS'
                                            : props.formState.values.durationType === value
                                    }
                                />
                                <FormLabel
                                    htmlFor={`durationType-${value}`}
                                    label={({
                                        HALF_DAY: props.i18n._('Half day'),
                                        FULL_DAY: props.i18n._('Full day'),
                                        MULTIPLE_DAYS: props.i18n._('Multiple days')
                                    } satisfies Record<typeof value, string>)[value]} // prettier-ignore
                                />
                            </div>
                        ),
                    )}
                </div>

                {props.isMultipleDays && (
                    <FormGroup labelSize="md" variant="flexSpacing" htmlFor="durationType">
                        <FormLabel className="flex w-fit cursor-pointer items-center gap-3">
                            <Checkbox
                                checked={
                                    props.formState.values.durationType ===
                                    'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY'
                                }
                                onChange={e =>
                                    props.formState.handleChange(
                                        'durationType',
                                        e.currentTarget.checked
                                            ? 'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY'
                                            : 'MULTIPLE_DAYS',
                                    )
                                }
                            />
                            {props.i18n._('With overnight stay?')}
                        </FormLabel>
                        <FormInputErrors
                            errors={props.formState.getErrorMessages('durationType')}
                        />
                    </FormGroup>
                )}
            </div>

            <div className="flex flex-col gap-5">
                <FormGroupHorizontal
                    className="*:flex-wrap"
                    description={props.i18n._('checkout.info.date.description')}
                    labelSize="md"
                    variant="no_spacing"
                    label={
                        props.isMultipleDays
                            ? props.i18n._('Select the dates')
                            : props.i18n._('Select the date')
                    }
                    required
                    htmlFor="participants"
                >
                    <div className="max-xs:flex-col flex w-full gap-x-5 gap-y-4 lg:w-fit lg:max-w-full">
                        <div className="w-full min-w-0 lg:w-40">
                            <DatePicker
                                value={props.formState.values.startsAt}
                                prefix={props.isMultipleDays ? props.i18n._('From') : undefined}
                                fromDate={new Date()}
                                data-testid="checkout-start-date"
                                onChange={date => {
                                    if (
                                        props.formState.values.durationType ===
                                            CART_DURATION_MULTIPLE_DAYS &&
                                        !props.formState.values.endsAt
                                    ) {
                                        props.formState.handleChange(
                                            'endsAt',
                                            date?.toISOString() ?? null,
                                        );
                                    }

                                    props.formState.handleChange(
                                        'startsAt',
                                        date?.toISOString() ?? null,
                                    );
                                }}
                                className={cx(
                                    'w-full lg:w-40',
                                    props.isMultipleDays && 'xs:rounded-r-none',
                                )}
                            />
                            <FormInputErrors
                                errors={props.formState.getErrorMessages('startsAt')}
                            />
                        </div>
                        {props.isMultipleDays && (
                            <div className="w-full min-w-0 lg:w-40">
                                <DatePicker
                                    prefix={props.i18n._('Until')}
                                    value={props.formState.values.endsAt}
                                    fromDate={new Date()}
                                    data-testid="checkout-end-date"
                                    onChange={date =>
                                        props.formState.handleChange(
                                            'endsAt',
                                            date?.toISOString() ?? null,
                                        )
                                    }
                                    className={cx('w-full xs:rounded-l-none')}
                                />
                                <FormInputErrors
                                    errors={props.formState.getErrorMessages('endsAt')}
                                />
                            </div>
                        )}
                    </div>
                </FormGroupHorizontal>

                <FormGroupHorizontal
                    label={props.i18n._('Select starting time')}
                    required
                    description={props.i18n._('checkout.info.starting_time.description')}
                    labelSize="md"
                    variant="no_spacing"
                    htmlFor="durationType"
                >
                    <div className="w-full min-w-0 lg:w-40">
                        <Input
                            id="startTime"
                            type="time"
                            className="w-full lg:w-40"
                            /** Component doesn't support null values */
                            value={props.formState.values.startTime ?? undefined}
                            onChange={event =>
                                props.formState.handleChange('startTime', event.target.value)
                            }
                        />
                        <FormInputErrors errors={props.formState.getErrorMessages('startTime')} />
                    </div>
                </FormGroupHorizontal>
            </div>
        </>
    );
};

namespace Checkout_Info_LocationsFieldset {
    export type FormValues = {
        address: string;
        radius: number;
        userProvidesLocation: boolean;
    };

    export const Component: React.FC<{
        formState: FormState.Subform<Checkout_Info_LocationsFieldset.FormValues>;
        i18n: I18n;
    }> = ({ formState, i18n }) => (
        <div>
            <FormGroup labelSize="md" label={i18n._('Select a location')} variant="no_spacing">
                <p className="text-sm text-blue-500">
                    {i18n._(
                        'This is only applicable for activities where you can choose the location',
                    )}
                </p>
            </FormGroup>
            <div className="col-span-2 mt-4 flex flex-row items-stretch">
                <div className="min-w-0 flex-1">
                    <GoogleMapsAutocomplete
                        onChange={value => formState.handleChange('address', value)}
                        value={formState.values.address}
                        placeholder={i18n._('Start typing an address and select from the list')}
                        searchPlaceHolder={i18n._('google-maps.search.placeholder')}
                    />
                </div>
                <InputSelect
                    className="h-auto shrink-0 whitespace-nowrap rounded-l-none lg:border-l-0"
                    value={String(formState.values.radius ?? 10)}
                    options={CHECKOUT_ADDRESS_RADIUS_KM_OPTIONS}
                    onChange={value => {
                        const next = Number(value);
                        if (!Number.isFinite(next) || formState.values.radius === next) return;
                        formState.handleChange('radius', next);
                    }}
                />
                <FormInputErrors errors={formState.getErrorMessages('address')} />
            </div>
        </div>
    );
}
